import axios, { AxiosInstance } from 'axios';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebaseClient';
import analytics, { errorCode } from './analytics';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://digitomy-backend-653857522996.us-central1.run.app/api';
const PROFILE_CACHE_KEY = 'digitomy_profile';

export interface Profile {
  id: string;
  username: string;
  displayName?: string | null;
  email: string | null;
  organizationId: string;
  role: 'PUBLIC' | 'EXPERT';
  firebaseUid: string | null;
  needsApproval: boolean;
  needsOrganization: boolean;
}

type AuthListener = (profile: Profile | null) => void;
type SyncErrorListener = (error: Error) => void;

class ApiService {
  private api: AxiosInstance;
  private profile: Profile | null;
  private listeners = new Set<AuthListener>();
  private syncErrorListeners = new Set<SyncErrorListener>();
  private firebaseResolved = false;

  constructor() {
    this.api = axios.create({
      baseURL: API_BASE_URL,
      headers: { 'Content-Type': 'application/json' },
    });

    this.profile = this.loadCachedProfile();

    // Attach a fresh Firebase ID token to every outbound request.
    this.api.interceptors.request.use(async (config) => {
      const user = auth.currentUser;
      if (user) {
        const token = await user.getIdToken();
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token rejected — drop the cached profile and let the app re-route to /auth.
          this.setProfile(null);
        }
        return Promise.reject(error);
      }
    );

    // React to Firebase auth changes (login, logout, token refresh).
    onAuthStateChanged(auth, async (user) => {
      this.firebaseResolved = true;
      if (!user) {
        this.setProfile(null);
        return;
      }
      try {
        const profile = await this.syncWithBackend();
        this.setProfile(profile);
      } catch (err) {
        analytics.capture('auth_sync_failed', { error_code: errorCode(err) });
        console.error('Failed to sync user with backend:', err);
        const wrapped =
          err instanceof Error ? err : new Error('Sync with backend failed');
        // Sign the user out of Firebase so the next attempt starts clean.
        await signOut(auth).catch(() => {});
        this.setProfile(null);
        this.syncErrorListeners.forEach((cb) => cb(wrapped));
      }
    });
  }

  /**
   * Fires when Firebase sign-in succeeded but /api/auth/sync failed. Useful for
   * surfacing a clear error in the UI instead of leaving the page in a half-signed-in
   * limbo. Returns an unsubscribe function.
   */
  onSyncError(listener: SyncErrorListener): () => void {
    this.syncErrorListeners.add(listener);
    return () => {
      this.syncErrorListeners.delete(listener);
    };
  }

  // ── Auth state ──────────────────────────────────────────────────────────

  /**
   * Subscribe to profile changes. Fires immediately with the current value
   * (which may be a stale cached profile) and again whenever Firebase reports
   * a state change. Returns an unsubscribe function.
   */
  onAuthChange(listener: AuthListener): () => void {
    this.listeners.add(listener);
    listener(this.profile);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Synchronous best-guess for "is the user signed in". May be stale until Firebase resolves. */
  isAuthenticated(): boolean {
    return this.profile !== null;
  }

  /** Synchronous best-guess for the current profile (may be null or stale). */
  getUser(): Profile | null {
    return this.profile;
  }

  hasResolved(): boolean {
    return this.firebaseResolved;
  }

  async logout(): Promise<void> {
    await signOut(auth);
    analytics.capture('logout_succeeded');
    this.setProfile(null);
  }

  // ── Backend sync ────────────────────────────────────────────────────────

  /**
   * Called automatically on Firebase sign-in. Posts the ID token to /api/auth/sync,
   * which creates-or-links the Postgres row and returns the user's role + org.
   */
  private async syncWithBackend(): Promise<Profile> {
    const user = auth.currentUser;
    if (!user) throw new Error('No Firebase user');
    const idToken = await user.getIdToken();
    const response = await this.api.post('/auth/sync', {}, {
      headers: { Authorization: `Bearer ${idToken}` },
    });
    const { user: u, needsApproval, needsOrganization } = response.data;
    // Registration saves the chosen name in Firebase. The database username
    // may have been generated from an email before that name was available.
    return {
      ...u,
      displayName: user.displayName?.trim() || null,
      needsApproval,
      needsOrganization,
    };
  }

  private setProfile(profile: Profile | null): void {
    analytics.setAuthenticatedProfile(profile);
    this.profile = profile;
    if (profile) {
      localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    } else {
      localStorage.removeItem(PROFILE_CACHE_KEY);
      // Also clear the legacy storage keys from the JWT era.
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    }
    this.listeners.forEach((cb) => cb(profile));
  }

  private loadCachedProfile(): Profile | null {
    try {
      const raw = localStorage.getItem(PROFILE_CACHE_KEY);
      return raw ? (JSON.parse(raw) as Profile) : null;
    } catch {
      return null;
    }
  }

  // ── Domain endpoints ────────────────────────────────────────────────────

  // Fetches the authenticated user's profile from the backend on demand.
  async getCurrentUser() {
    const response = await this.api.get('/auth/me');
    analytics.setAuthenticatedProfile(response.data.user);
    return response.data;
  }

  // Mosquito Image endpoints
  async getAvailableImages(limit: number = 10, page: number = 1) {
    const response = await this.api.get('/mosquito-images', {
      params: { limit, page },
    });
    return response.data;
  }

  async getNextUnannotatedImage(afterId?: string) {
    const response = await this.api.get('/mosquito-images/next-unannotated', {
      params: afterId ? { after: afterId } : undefined,
    });
    return response.data;
  }

  async getImageById(id: string) {
    const response = await this.api.get(`/mosquito-images/${id}`);
    return response.data;
  }

  async getUserStats() {
    const response = await this.api.get('/mosquito-images/stats');
    return response.data;
  }

  // Species endpoints
  async getSpeciesList() {
    const response = await this.api.get('/species');
    return response.data;
  }

  async getSpeciesForGenus(genus: string) {
    const response = await this.api.get(`/species/${genus}`);
    return response.data;
  }

  async createAnnotation(
    mosquitoImageId: string,
    genus?: string,
    species?: string,
    gender?: string,
    explanation?: string,
    other?: string
  ) {
    const response = await this.api.post('/annotations', {
      mosquitoImageId,
      genus,
      species,
      gender,
      explanation,
      other,
    });
    return response.data;
  }

  async getUserAnnotations(limit: number = 50, page: number = 1) {
    const response = await this.api.get('/annotations/my-annotations', {
      params: { limit, page },
    });
    return response.data;
  }

  async updateAnnotation(
    id: string,
    data: {
      genus?: string;
      species?: string;
      gender?: string;
      explanation?: string;
    }
  ) {
    const response = await this.api.put(`/annotations/${id}`, data);
    return response.data;
  }

  async deleteAnnotation(id: string) {
    const response = await this.api.delete(`/annotations/${id}`);
    return response.data;
  }

  async getAnnotationStats() {
    const response = await this.api.get('/annotations/stats');
    return response.data;
  }

  // Capture sessions for the gallery (each session has up to 2 cameras).
  async getCaptureSessions(limit: number = 12, page: number = 1) {
    const response = await this.api.get('/processed-images', {
      params: { limit, page },
    });
    return response.data;
  }

  // Contact endpoints
  async submitContactMessage(email: string, message: string) {
    const response = await this.api.post('/contact', {
      email,
      message,
    });
    return response.data;
  }
}

const apiService = new ApiService();
export default apiService;
